import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import CancelIcon from '@mui/icons-material/Cancel';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';

import CreateFooter from '@common/components/CreateFooter/CreateFooter';
import LoaderButton from '@common/components/LoaderButton/LoaderButton';

import CreateDetailsForm from './CreateDetailsForm';

import { TEMPLATES_PATHS } from '@common/network/ApiPaths';

import useFormValidation from './useFormValidation';
import { NEW_TEMPLATE_STEPS, PAGE_ROUTES } from '@common/constants';
import {
  isCreateOk,
  isResponseBarRequest,
  isResponseOk,
} from '@common/helpers/helpers';
import {
  notifySuccess,
  notifyError,
  buildUpdatedValues,
  isDetailsStep,
  isSubjectAndBodyStep,
  isPreviewStep,
} from '@common/helpers/createNewHelpers';
import Notification, {
  useNotification,
} from '@common/components/Notification/Notification';

import { patchByPathAndData, postByPathAndData } from '@services/newServices';
import TedTheme from '@common/providers/ThemeProvider/TedTheme';
import { selectBackgroundColor } from '@common/providers/ThemeProvider/selectors';
import { useTemplateDetailsStore } from '@store/templateDetailsStore';
import TemplateEditor from '@pages/TemplateEditor/TemplateEditor';
import PreviewStep from './PreviewStep';

const MyStepper = ({ activeStep }) => {
  const { t } = useTranslation();
  return (
    <Stepper
      sx={{
        pt: '1rem',
        width: '60%',
        minWidth: '500px',
        pb: '1rem',
        backgroundColor: selectBackgroundColor,
        zIndex: 4,
        position: 'relative',
        maxWidth: '800px',
        width: '100%',
        margin: '0 auto',
        px: { xs: 0, sm: 3 }, // padding sa strane na malim ekranima
      }}
      activeStep={activeStep}
    >
      <Step>
        <StepLabel>{t('template_details')}</StepLabel>
      </Step>
      <Step>
        <StepLabel>{t('email_subject_and_body')}</StepLabel>
      </Step>
      <Step>
        <StepLabel>{t('preview_title')}</StepLabel>
      </Step>
    </Stepper>
  );
};

const createOrEditCall = async ({ updatedValues, editId }) => {
  if (editId) {
    return await patchByPathAndData({
      path: TEMPLATES_PATHS.PATCH_BY_ID,
      pathVariables: { id: editId },
      data: updatedValues,
      responseType: 'blob',
    });
  } else {
    return await postByPathAndData({
      path: TEMPLATES_PATHS.POST,
      data: updatedValues,
      responseType: 'blob',
    });
  }
};

const NewTemplate = ({ editId }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const templateState = useTemplateDetailsStore();

  const setInitState = useTemplateDetailsStore((s) => s.setInitState);
  const setType = useTemplateDetailsStore((s) => s.setType);

  const [activeStep, setActiveStep] = useState(NEW_TEMPLATE_STEPS.DETAILS);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const validation = useFormValidation(templateState);

  const [showStatuses, showNotifications, handleClose] = useNotification();

  const onSaveClick = async (values) => {
    const invalidEditors = values?.editor?.filter(({ isValid }) => !isValid);

    try {
      if (!invalidEditors?.length) {
        setIsSubmitting(true);
        const updatedValues = buildUpdatedValues(values);

        const res = await createOrEditCall({ updatedValues, editId });

        if (isCreateOk(res)) {
          notifySuccess(showNotifications, t('save_success'));
          setTimeout(() => {
            navigate(PAGE_ROUTES.TEMPLATES);
          }, 1000);
        }

        if (isResponseOk(res)) {
          notifySuccess(showNotifications, t('save_success'));
        }
      } else {
        const messages = invalidEditors.reduce(
          (aggErrorMessage, editorState) => {
            if (aggErrorMessage) {
              aggErrorMessage = `${aggErrorMessage}, ${editorState.errorMessages}`;
            } else {
              aggErrorMessage = editorState.errorMessages;
            }
            return aggErrorMessage;
          },
          '',
        );

        notifyError(showNotifications, messages || t('save_error'));
      }
    } catch (err) {
      console.log('err: ', err);
      if (isResponseBarRequest(err)) {
        notifyError(showNotifications, err?.message || t('save_error'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCancel = () => {
    navigate(PAGE_ROUTES.TEMPLATES);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  useEffect(() => {
    !editId && setType('email');
  }, [editId]);

  useEffect(() => {
    return () => setInitState();
  }, []);

  return (
    <>
      <TedTheme>
        <MyStepper activeStep={activeStep} />
        {isDetailsStep(activeStep) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              backgroundColor: '#e0e0e0',
              zIndex: 4,
              position: 'relative',
              height: 'calc(100vh - 160px)',
              overflowY: 'auto',
            }}
          >
            <CreateDetailsForm
              validation={validation}
              isEditMode={Boolean(editId)}
            />
          </Box>
        )}
        {isSubjectAndBodyStep(activeStep) ? (
          <Box sx={{ pt: 3 }}>
            <TemplateEditor />
          </Box>
        ) : null}

        {isPreviewStep(activeStep) && (
          <Box sx={{ pt: 3 }}>
            <PreviewStep />
          </Box>
        )}

        <CreateFooter isLoading={isSubmitting}>
          <Button
            data-test-id='cancel-btn'
            variant='outlined'
            startIcon={<CancelIcon />}
            color='error'
            onClick={onCancel}
            sx={{ mr: 1 }}
            size='small'
          >
            {t('cancel_button')}
          </Button>
          <Button
            data-test-id='back-btn'
            color='primary'
            disabled={isDetailsStep(activeStep)}
            onClick={handleBack}
            variant='outlined'
            sx={{ mr: 1 }}
            startIcon={<NavigateBeforeIcon />}
            size='small'
          >
            {t('back_button')}
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />

          {isPreviewStep(activeStep) ? (
            <LoaderButton
              isSubmitting={isSubmitting}
              isValid={validation?.isValid}
              onClick={() => onSaveClick(templateState)}
              submitText={'creating'}
              defaultText={'save_button'}
              size='small'
            />
          ) : (
            <Button
              size='small'
              data-test-id='next-btn'
              endIcon={<NavigateNextIcon />}
              variant='outlined'
              disabled={!validation?.isValid}
              onClick={handleNext}
            >
              {t('next')}
            </Button>
          )}
        </CreateFooter>

        <Notification showStatuses={showStatuses} handleClose={handleClose} />
      </TedTheme>
    </>
  );
};

export default NewTemplate;
